/**
 * High-performance symbols store with batched updates
 */

import { create } from 'zustand';
import type { Symbol, SymbolsBatch, SymbolsState, SymbolsActions, SymbolGroupTemplate } from '../types';

interface SymbolsStore extends SymbolsState, SymbolsActions {}

/** Matches MT5-style wildcard masks in the last path segment (e.g. "*", "EUR*"). */
const MASK_PATTERN = /[*?]/;

const equalsIgnoreCase = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'accent' }) === 0;

/**
 * Protocol-aware, separator-tolerant group-path parsing.
 *
 * Splits a groupName on backslashes and/or forward slashes and returns only the
 * folder segments:
 * - The trailing segment is dropped when it is a symbol identifier — i.e. it
 *   case-insensitively matches one of `symbolIdentifiers` (usually the symbol
 *   currencies in the group) or is a wildcard mask ("*", "EUR*") as MT5 sends.
 * - Otherwise every segment is a folder, so flat single-segment labels like
 *   "Majors" become a real folder instead of being skipped.
 * - An empty groupName yields [] (root).
 */
export const getGroupFolderSegments = (
  groupName: string,
  symbolIdentifiers: readonly string[] = []
): string[] => {
  const segments = groupName.split(/[\\/]+/).filter(Boolean);
  if (segments.length === 0) return [];

  const last = segments[segments.length - 1];
  const isSymbolLeaf =
    MASK_PATTERN.test(last) ||
    symbolIdentifiers.some((identifier) => equalsIgnoreCase(last, identifier));

  return isSymbolLeaf ? segments.slice(0, -1) : segments;
};

/** Folder path for a single symbol, derived from its groupName. Empty array = root. */
export const getGroupFolderPath = (
  symbol: Pick<Symbol, 'groupName' | 'currency'>
): string[] => getGroupFolderSegments(symbol.groupName, [symbol.currency]);

/** Folder segments for a byGroup entry (groupName + the symbols filed under it). */
const getFolderSegmentsForGroup = (
  groupName: string,
  symbols: readonly Symbol[]
): string[] =>
  getGroupFolderSegments(
    groupName,
    symbols.map((symbol) => symbol.currency)
  );

const initialState: SymbolsState = {
  byId: new Map(),
  byCurrency: new Map(),
  byGroup: new Map(),
  loadingStatus: 'idle',
  totalExpected: 0,
  totalLoaded: 0,
  batchesReceived: 0,
  totalBatches: 0,
  errorMessage: null,
  baseInfo: null,
  groupTemplates: [],
  allSymbols: [],
  groupNames: [],
};

export const useSymbolsStore = create<SymbolsStore>((set, get) => {
  // Create stable function references
  const getSymbolById = (id: number) => get().byId.get(id);
  const getSymbolByCurrency = (currency: string) => get().byCurrency.get(currency);
  const findSymbolByCurrency = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    const exact = get().byCurrency.get(trimmed);
    if (exact) return exact;
    for (const [key, symbol] of get().byCurrency) {
      if (key.localeCompare(trimmed, undefined, { sensitivity: "accent" }) === 0) {
        return symbol;
      }
    }
    return undefined;
  };
  const getSymbolsByGroup = (groupName: string) => get().byGroup.get(groupName) || [];
  const getGroupTemplates = () => get().groupTemplates;
  const isComplete = () => get().loadingStatus === 'complete';

  // Helper to get multiple symbols by currency names
  const getSymbolsByCurrencies = (currencies: string[]) => {
    const symbols: Symbol[] = [];
    const state = get();
    
    for (const currency of currencies) {
      const symbol = state.byCurrency.get(currency);
      if (symbol) {
        symbols.push(symbol);
      }
    }
    
    return symbols;
  };

  // Helper to get symbols by search query
  const getSymbolsByQuery = (query: string) => {
    if (!query.trim()) return [];
    
    const lowerQuery = query.toLowerCase();
    const results: Symbol[] = [];
    
    // Search in both currency and description
    get().byCurrency.forEach((symbol) => {
      if (symbol.currency.toLowerCase().includes(lowerQuery) ||
          symbol.description.toLowerCase().includes(lowerQuery)) {
        results.push(symbol);
      }
    });
    
    return results;
  };

  // Helper to get catalogs structure by group path
  const getCatalogsByGroup = (path: string[] = []) => {
    const state = get();
    const groupsMap = new Map<string, { 
      name: string; 
      hasSubgroups: boolean;
      symbolsCount: number;
      path: string[];
    }>();
    
    // Build current path string
    const currentPathStr = path.length > 0 ? path.join('\\') + '\\' : '';
    
    // Find all groups that start with current path
    state.byGroup.forEach((symbols, groupName) => {
      // Separator-tolerant split with the trailing symbol identifier removed
      const folderParts = getFolderSegmentsForGroup(groupName, symbols);
      if (folderParts.length === 0) return; // Root-level symbols have no folder

      const folderPath = folderParts.join('\\');

      if (!(folderPath + '\\').startsWith(currentPathStr)) return;
      
      // Remove current path prefix
      const remainingPath = folderPath.slice(currentPathStr.length);
      const parts = remainingPath.split('\\').filter(Boolean);
      
      if (parts.length === 0) return; // Skip exact match
      
      // Get next level folder
      const nextFolder = parts[0];
      const nextPath = [...path, nextFolder];
      const nextPathStr = nextPath.join('\\');
      
      if (!groupsMap.has(nextFolder)) {
        // Check if this folder has subfolders
        let hasSubgroups = false;
        let totalSymbols = 0;
        
        // Count all symbols in this folder and subfolders
        state.byGroup.forEach((syms, gName) => {
          const gParts = getFolderSegmentsForGroup(gName, syms);
          if (gParts.length === 0) return;

          const gFolderPath = gParts.join('\\');
          
          // Check if this group belongs to the current folder
          // Must match exactly or be a subfolder (with backslash separator)
          if (gFolderPath === nextPathStr || gFolderPath.startsWith(nextPathStr + '\\')) {
            totalSymbols += syms.length;
            // Check if there are deeper levels
            const subPath = gFolderPath.slice(nextPathStr.length);
            if (subPath.startsWith('\\') && subPath.slice(1).length > 0) {
              hasSubgroups = true;
            }
          }
        });
        
        groupsMap.set(nextFolder, {
          name: nextFolder,
          hasSubgroups,
          symbolsCount: totalSymbols,
          path: nextPath
        });
      }
    });
    
    // Convert to array and sort
    return Array.from(groupsMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Helper to get symbols for a specific group path
  const getSymbolsByGroupPath = (path: string[]) => {
    if (path.length === 0) {
      // Root level - return symbols whose group resolves to no folder
      const rootSymbols: Symbol[] = [];
      get().byCurrency.forEach((symbol) => {
        if (getGroupFolderPath(symbol).length === 0) {
          rootSymbols.push(symbol);
        }
      });
      return rootSymbols;
    }

    const pathStr = path.join('\\');
    const symbols: Symbol[] = [];

    // Get symbols that belong to this path
    get().byGroup.forEach((groupSymbols, groupName) => {
      const folderParts = getFolderSegmentsForGroup(groupName, groupSymbols);
      if (folderParts.length === 0) return; // Root-level symbols have no folder

      // Include symbols if folder path matches exactly
      if (folderParts.join('\\') === pathStr) {
        symbols.push(...groupSymbols);
      }
    });

    return symbols;
  };

  return {
    ...initialState,

    processBatch: (batch: SymbolsBatch) => {
    set((state) => {
      // Handle errors
      if (!batch.success) {
        return {
          ...state,
          loadingStatus: 'error',
          errorMessage: batch.errorMessage,
        };
      }

      // `batchNumber === 0` marks the start of a fresh sync (initial load *or* a
      // re-sync after re-login/reconnect). Start from empty accumulators so a
      // re-sync replaces the previous data instead of appending to it — otherwise
      // the `byGroup` arrays accumulate duplicate symbols on every reconnect,
      // which surfaces as duplicated rows in the symbol browser.
      const isSyncStart = batch.batchNumber === 0;

      // Initialize on the first batch of a sync (computed immutably — never mutate `state`)
      const totalExpected = isSyncStart ? batch.totalSymbolsCount : state.totalExpected;
      const totalBatches = isSyncStart ? batch.totalBatches : state.totalBatches;

      // Clone maps for immutability (Zustand requirement); on sync start begin fresh.
      const newById = isSyncStart ? new Map<number, Symbol>() : new Map(state.byId);
      const newByCurrency = isSyncStart
        ? new Map<string, Symbol>()
        : new Map(state.byCurrency);
      const newByGroup = isSyncStart
        ? new Map<string, Symbol[]>()
        : new Map(state.byGroup);

      // Process symbols in batch - high performance loop
      for (let i = 0; i < batch.symbols.length; i++) {
        const symbol = batch.symbols[i];
        
        // Add to lookup maps
        newById.set(symbol.id, symbol);
        newByCurrency.set(symbol.currency, symbol);
        
        // Group by groupName
        const groupName = symbol.groupName;
        if (!newByGroup.has(groupName)) {
          newByGroup.set(groupName, []);
        }
        newByGroup.get(groupName)!.push(symbol);
      }

      // Update counters (reset the running totals when a fresh sync starts)
      const newBatchesReceived = (isSyncStart ? 0 : state.batchesReceived) + 1;
      const newTotalLoaded = (isSyncStart ? 0 : state.totalLoaded) + batch.symbols.length;
      const isComplete =
        batch.isFinalBatch ||
        (totalExpected > 0 && newTotalLoaded >= totalExpected) ||
        batch.batchNumber + 1 === totalBatches;

      let newGroupTemplates = isSyncStart ? [] : state.groupTemplates;
      if (batch.groups?.length) {
        const byName = new Map(newGroupTemplates.map((g) => [g.groupName, g]));
        for (const group of batch.groups) {
          byName.set(group.groupName, group);
        }
        newGroupTemplates = Array.from(byName.values()).sort((a, b) =>
          a.groupName.localeCompare(b.groupName)
        );
      }

      // Compute sorted arrays only when complete (expensive operation)
      let newAllSymbols = state.allSymbols;
      let newGroupNames = state.groupNames;
      
      if (isComplete) {
        // Derive from byCurrency (keyed by unique currency) — byId is only a
        // best-effort lookup: gateways can emit 0/duplicate ids, which would
        // silently overwrite entries and truncate allSymbols.
        newAllSymbols = Array.from(newByCurrency.values()).sort((a, b) =>
          a.currency.localeCompare(b.currency)
        );
        newGroupNames = Array.from(newByGroup.keys()).sort();
      }

      return {
        ...state,
        byId: newById,
        byCurrency: newByCurrency,
        byGroup: newByGroup,
        batchesReceived: newBatchesReceived,
        totalLoaded: newTotalLoaded,
        totalExpected,
        totalBatches,
        baseInfo: batch.baseInfo ?? state.baseInfo,
        groupTemplates: newGroupTemplates,
        loadingStatus: isComplete ? 'complete' : 'loading',
        allSymbols: newAllSymbols,
        groupNames: newGroupNames,
      };
    });
  },

  reset: () => set(initialState),

  // Stable function references
  getSymbolById,
  getSymbolByCurrency,
  findSymbolByCurrency,
  getSymbolsByGroup,
  isComplete,
  getSymbolsByCurrencies,
  getSymbolsByQuery,
  getCatalogsByGroup,
  getSymbolsByGroupPath,
  getGroupTemplates,
};
});
